/**
 * Slide-in motion for the Compare Models rating boxes (snippets/rating-scales.liquid).
 *
 * Same feel as the old PLP lineup sliders (launch-plp.js, "STAT SLIDERS"): each
 * filled box starts at Low and slides to its level the first time its column's
 * boxes scroll into view (top 85%), 0.8s power3.out, staggered 0.05s per row.
 * The trigger is the column's first row of boxes, not the column, because the
 * boxes sit well below the product image and would finish sliding off-screen.
 *
 * Styles are untouched. The Liquid output already shows every box at its level,
 * which is what no-JS, reduced motion and rows with more than one filled box
 * keep. While parked and sliding, a puck inside box 1 (inset 0, the filled
 * box's own colour) stands in for the filled box, and the real box shows as
 * empty. Both are removed when the puck lands, leaving the original markup.
 */

const COLUMN = '.pdp-model-comparison .model-compare-column';
const ROW = '.rating-scale--boxes';

class CompareSliders {
  constructor() {
    if (!window.gsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const columns = [...document.querySelectorAll(COLUMN)]
      .map((column) => ({ trigger: column.querySelector(ROW), rows: this.park(column) }))
      .filter(({ rows }) => rows.length);
    if (!columns.length) return;

    const byTrigger = new Map(columns.map(({ trigger, rows }) => [trigger, rows]));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          this.slide(byTrigger.get(entry.target));
        });
      },
      { rootMargin: '0px 0px -15% 0px' }
    );
    byTrigger.forEach((rows, trigger) => observer.observe(trigger));
  }

  /**
   * Shows each single-level row at Low. Rows already at Low have nothing to slide.
   * @param {Element} column
   */
  park(column) {
    return [...column.querySelectorAll(ROW)].flatMap((row) => {
      const boxes = [...row.querySelectorAll('.rating-scale__box')];
      const filled = boxes.filter((box) => box.classList.contains('filled'));
      if (filled.length !== 1 || filled[0] === boxes[0]) return [];

      const [target] = filled;
      const first = boxes[0];
      const puck = document.createElement('span');
      puck.setAttribute('aria-hidden', 'true');
      Object.assign(puck.style, {
        position: 'absolute',
        inset: '0',
        backgroundColor: getComputedStyle(target).backgroundColor,
      });

      first.style.position = 'relative';
      first.append(puck);
      target.style.transition = 'none';
      target.style.backgroundColor = getComputedStyle(first).backgroundColor;

      return [{ first, target, puck }];
    });
  }

  /** @param {{ first: HTMLElement, target: HTMLElement, puck: HTMLElement }[]} rows */
  slide(rows) {
    rows.forEach(({ first, target, puck }, i) => {
      window.gsap.to(puck, {
        x: () => target.offsetLeft - first.offsetLeft,
        duration: 0.8,
        ease: 'power3.out',
        delay: i * 0.05,
        onComplete: () => {
          target.style.backgroundColor = '';
          target.offsetWidth; // apply the colour before the transition comes back
          target.style.transition = '';
          puck.remove();
          first.style.position = '';
        },
      });
    });
  }
}

new CompareSliders();
